/**
 * SKOP AI Trade Summaries
 *
 * Generates plain English explanations for on-chain transactions.
 * Makes complex blockchain activity understandable for all users.
 */

// Transaction types we can summarize
export type TransactionType =
  | 'buy'
  | 'sell'
  | 'whale_buy'
  | 'whale_sell'
  | 'creator_sell'
  | 'bundled_buy'
  | 'large_transfer'
  | 'liquidity_add'
  | 'liquidity_remove'
  | 'graduation';

export interface Transaction {
  signature: string;
  type: TransactionType;
  timestamp: number;
  walletAddress: string;
  tokenAmount: number;
  solAmount: number;
  pricePerToken?: number;
  // Context
  walletLabel?: string; // "Creator", "Whale #3", etc.
  isCreator?: boolean;
  isWhale?: boolean;
  holdingPercentBefore?: number;
  holdingPercentAfter?: number;
  tokenSymbol: string;
  // For bundled buys
  bundleSize?: number;
  bundlePosition?: number;
}

export interface TradeSummary {
  headline: string; // Short summary (1 line)
  description: string; // Detailed explanation (2-3 sentences)
  sentiment: 'bullish' | 'bearish' | 'neutral' | 'warning';
  significance: 'low' | 'medium' | 'high' | 'critical';
  emoji: string;
}

/**
 * Generate a plain English summary of a transaction
 */
export function summarizeTransaction(tx: Transaction): TradeSummary {
  switch (tx.type) {
    case 'whale_buy':
      return summarizeWhaleBuy(tx);
    case 'whale_sell':
      return summarizeWhaleSell(tx);
    case 'creator_sell':
      return summarizeCreatorSell(tx);
    case 'bundled_buy':
      return summarizeBundledBuy(tx);
    case 'buy':
      return summarizeRegularBuy(tx);
    case 'sell':
      return summarizeRegularSell(tx);
    case 'large_transfer':
      return summarizeLargeTransfer(tx);
    case 'graduation':
      return summarizeGraduation(tx);
    case 'liquidity_add':
      return summarizeLiquidityAdd(tx);
    case 'liquidity_remove':
      return summarizeLiquidityRemove(tx);
    default:
      return {
        headline: 'Transaction detected',
        description: `A transaction occurred involving ${formatTokenAmount(tx.tokenAmount)} ${tx.tokenSymbol}.`,
        sentiment: 'neutral',
        significance: 'low',
        emoji: 'default',
      };
  }
}

function summarizeWhaleBuy(tx: Transaction): TradeSummary {
  const label = tx.walletLabel || 'A whale';
  const solFormatted = tx.solAmount.toFixed(2);
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);
  const percentAfter = tx.holdingPercentAfter?.toFixed(1) || '?';

  return {
    headline: `${label} bought ${solFormatted} SOL of ${tx.tokenSymbol}`,
    description: `${label} just accumulated ${tokenFormatted} ${tx.tokenSymbol} for ${solFormatted} SOL. ` +
      `They now hold approximately ${percentAfter}% of the supply. ` +
      `Large buys like this often signal confidence in the token.`,
    sentiment: 'bullish',
    significance: tx.solAmount > 10 ? 'high' : 'medium',
    emoji: 'whale',
  };
}

function summarizeWhaleSell(tx: Transaction): TradeSummary {
  const label = tx.walletLabel || 'A whale';
  const solFormatted = tx.solAmount.toFixed(2);
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);
  const percentBefore = tx.holdingPercentBefore?.toFixed(1) || '?';
  const percentAfter = tx.holdingPercentAfter?.toFixed(1) || '?';

  const soldAll = tx.holdingPercentAfter === 0;

  return {
    headline: soldAll
      ? `${label} exited their entire position`
      : `${label} sold ${solFormatted} SOL of ${tx.tokenSymbol}`,
    description: soldAll
      ? `${label} has completely exited their ${tx.tokenSymbol} position, selling ${tokenFormatted} tokens for ${solFormatted} SOL. ` +
        `When whales fully exit, it can indicate they've lost confidence in the token.`
      : `${label} reduced their position from ${percentBefore}% to ${percentAfter}%, selling ${tokenFormatted} ${tx.tokenSymbol} for ${solFormatted} SOL. ` +
        `This could be profit-taking or a sign of reduced confidence.`,
    sentiment: soldAll ? 'warning' : 'bearish',
    significance: soldAll ? 'critical' : tx.solAmount > 10 ? 'high' : 'medium',
    emoji: soldAll ? 'alert' : 'down',
  };
}

function summarizeCreatorSell(tx: Transaction): TradeSummary {
  const solFormatted = tx.solAmount.toFixed(2);
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);
  const percentBefore = tx.holdingPercentBefore?.toFixed(1) || '?';
  const percentAfter = tx.holdingPercentAfter?.toFixed(1) || '?';

  const soldAll = tx.holdingPercentAfter === 0;
  const soldMost = (tx.holdingPercentAfter || 0) < 1 && !soldAll;

  if (soldAll) {
    return {
      headline: `Creator dumped entire position!`,
      description: `The token creator has sold ALL of their ${tx.tokenSymbol} holdings (${tokenFormatted} tokens) for ${solFormatted} SOL. ` +
        `This is a major red flag - when creators fully exit, the token often crashes.`,
      sentiment: 'warning',
      significance: 'critical',
      emoji: 'alert',
    };
  }

  if (soldMost) {
    return {
      headline: `Creator sold most of their tokens`,
      description: `The creator reduced their ${tx.tokenSymbol} position from ${percentBefore}% to just ${percentAfter}%, ` +
        `selling ${tokenFormatted} tokens for ${solFormatted} SOL. Very little skin left in the game.`,
      sentiment: 'warning',
      significance: 'critical',
      emoji: 'warning',
    };
  }

  return {
    headline: `Creator sold ${solFormatted} SOL of ${tx.tokenSymbol}`,
    description: `The token creator reduced their position from ${percentBefore}% to ${percentAfter}%, ` +
      `selling ${tokenFormatted} ${tx.tokenSymbol} for ${solFormatted} SOL. ` +
      `Some selling is normal, but watch for continued dumps.`,
    sentiment: 'bearish',
    significance: 'high',
    emoji: 'eye',
  };
}

function summarizeBundledBuy(tx: Transaction): TradeSummary {
  const bundleInfo = tx.bundleSize
    ? ` (${tx.bundlePosition} of ${tx.bundleSize} in bundle)`
    : '';

  return {
    headline: `Bundled buy detected${bundleInfo}`,
    description: `This buy was part of a coordinated bundle in the same block as token creation. ` +
      `Bundled buys often indicate insider activity or launch manipulation. ` +
      `The buyer acquired ${formatTokenAmount(tx.tokenAmount)} ${tx.tokenSymbol} for ${tx.solAmount.toFixed(2)} SOL.`,
    sentiment: 'warning',
    significance: 'high',
    emoji: 'target',
  };
}

function summarizeRegularBuy(tx: Transaction): TradeSummary {
  const solFormatted = tx.solAmount.toFixed(2);
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);

  // Size-based messaging
  if (tx.solAmount >= 5) {
    return {
      headline: `Large buy: ${solFormatted} SOL`,
      description: `Someone bought ${tokenFormatted} ${tx.tokenSymbol} for ${solFormatted} SOL. ` +
        `This is a notable purchase that shows buying interest.`,
      sentiment: 'bullish',
      significance: 'medium',
      emoji: 'money',
    };
  }

  return {
    headline: `Buy: ${solFormatted} SOL`,
    description: `${tokenFormatted} ${tx.tokenSymbol} purchased for ${solFormatted} SOL.`,
    sentiment: 'bullish',
    significance: 'low',
    emoji: 'up',
  };
}

function summarizeRegularSell(tx: Transaction): TradeSummary {
  const solFormatted = tx.solAmount.toFixed(2);
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);

  if (tx.solAmount >= 5) {
    return {
      headline: `Large sell: ${solFormatted} SOL`,
      description: `Someone sold ${tokenFormatted} ${tx.tokenSymbol} for ${solFormatted} SOL. ` +
        `Large sells can put downward pressure on price.`,
      sentiment: 'bearish',
      significance: 'medium',
      emoji: 'down',
    };
  }

  return {
    headline: `Sell: ${solFormatted} SOL`,
    description: `${tokenFormatted} ${tx.tokenSymbol} sold for ${solFormatted} SOL.`,
    sentiment: 'bearish',
    significance: 'low',
    emoji: 'down',
  };
}

function summarizeLargeTransfer(tx: Transaction): TradeSummary {
  const tokenFormatted = formatTokenAmount(tx.tokenAmount);

  return {
    headline: `Large transfer detected`,
    description: `${tokenFormatted} ${tx.tokenSymbol} was transferred to a new wallet. ` +
      `This could be moving to a new wallet, preparing to sell, or distributing to team members.`,
    sentiment: 'neutral',
    significance: 'medium',
    emoji: 'send',
  };
}

function summarizeGraduation(tx: Transaction): TradeSummary {
  return {
    headline: `${tx.tokenSymbol} has graduated!`,
    description: `${tx.tokenSymbol} has completed its bonding curve and graduated to a DEX! ` +
      `The token is now trading on Raydium/PumpSwap with full liquidity. ` +
      `Graduation is a significant milestone - only ~2% of pump.fun tokens make it this far.`,
    sentiment: 'bullish',
    significance: 'critical',
    emoji: 'graduation',
  };
}

function summarizeLiquidityAdd(tx: Transaction): TradeSummary {
  return {
    headline: `Liquidity added`,
    description: `${tx.solAmount.toFixed(2)} SOL of liquidity was added to the ${tx.tokenSymbol} pool. ` +
      `More liquidity means less slippage and more stable trading.`,
    sentiment: 'bullish',
    significance: 'medium',
    emoji: 'water',
  };
}

function summarizeLiquidityRemove(tx: Transaction): TradeSummary {
  return {
    headline: `Liquidity removed`,
    description: `${tx.solAmount.toFixed(2)} SOL of liquidity was removed from the ${tx.tokenSymbol} pool. ` +
      `Removing liquidity can increase price volatility and slippage.`,
    sentiment: 'warning',
    significance: 'high',
    emoji: 'water',
  };
}

/**
 * Format large token amounts for readability
 */
function formatTokenAmount(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(2)}B`;
  }
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(2)}K`;
  }
  return amount.toFixed(2);
}

/**
 * Get sentiment color for UI
 */
export function getSentimentColor(sentiment: TradeSummary['sentiment']): string {
  switch (sentiment) {
    case 'bullish':
      return 'var(--success)';
    case 'bearish':
      return 'var(--error)';
    case 'warning':
      return 'var(--warning)';
    default:
      return 'var(--text-secondary)';
  }
}

/**
 * Get significance color for UI
 */
export function getSignificanceColor(significance: TradeSummary['significance']): string {
  switch (significance) {
    case 'critical':
      return 'var(--error)';
    case 'high':
      return 'var(--warning)';
    case 'medium':
      return 'var(--accent)';
    default:
      return 'var(--text-tertiary)';
  }
}

/**
 * Batch summarize multiple transactions into a feed
 */
export function summarizeTransactionFeed(
  transactions: Transaction[],
  limit: number = 10
): Array<Transaction & { summary: TradeSummary }> {
  return transactions
    .slice(0, limit)
    .map((tx) => ({
      ...tx,
      summary: summarizeTransaction(tx),
    }));
}

/**
 * Generate a "what just happened" recap for a token
 */
export function generateTokenRecap(
  tokenSymbol: string,
  transactions: Transaction[],
  timeWindowMinutes: number = 60
): string {
  const cutoff = Date.now() - timeWindowMinutes * 60 * 1000;
  const recentTxs = transactions.filter((tx) => tx.timestamp > cutoff);

  if (recentTxs.length === 0) {
    return `No significant activity on ${tokenSymbol} in the last ${timeWindowMinutes} minutes.`;
  }

  // Aggregate stats
  const buys = recentTxs.filter((tx) => tx.type === 'buy' || tx.type === 'whale_buy');
  const sells = recentTxs.filter((tx) => tx.type === 'sell' || tx.type === 'whale_sell' || tx.type === 'creator_sell');
  const totalBuyVolume = buys.reduce((sum, tx) => sum + tx.solAmount, 0);
  const totalSellVolume = sells.reduce((sum, tx) => sum + tx.solAmount, 0);

  const criticalEvents = recentTxs.filter((tx) => {
    const summary = summarizeTransaction(tx);
    return summary.significance === 'critical';
  });

  let recap = `In the last ${timeWindowMinutes} minutes:\n`;

  if (criticalEvents.length > 0) {
    const event = summarizeTransaction(criticalEvents[0]);
    recap += `\n[ALERT] ${event.headline}\n`;
  }

  recap += `\n• ${buys.length} buys totaling ${totalBuyVolume.toFixed(2)} SOL`;
  recap += `\n• ${sells.length} sells totaling ${totalSellVolume.toFixed(2)} SOL`;

  const netFlow = totalBuyVolume - totalSellVolume;
  if (netFlow > 0) {
    recap += `\n\n[+] Net inflow: +${netFlow.toFixed(2)} SOL (bullish)`;
  } else if (netFlow < 0) {
    recap += `\n\n[-] Net outflow: ${netFlow.toFixed(2)} SOL (bearish)`;
  } else {
    recap += `\n\n[=] Balanced flow`;
  }

  return recap;
}

/**
 * Generate mock transactions for demonstration
 */
export function generateMockTransactions(tokenAddress: string, count: number = 15): Transaction[] {
  const types: TransactionType[] = [
    'buy', 'buy', 'buy', 'sell', 'sell',
    'whale_buy', 'whale_sell', 'creator_sell', 'bundled_buy',
  ];

  const transactions: Transaction[] = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const isBuy = type.includes('buy');
    const isWhale = type.includes('whale');
    const isCreator = type === 'creator_sell';

    const solAmount = isWhale
      ? 5 + Math.random() * 45 // 5-50 SOL for whales
      : 0.1 + Math.random() * 2; // 0.1-2 SOL for regular

    transactions.push({
      signature: `mock_${tokenAddress.slice(0, 8)}_${i}_${Date.now()}`,
      type,
      timestamp: now - (i * (2 + Math.random() * 10) * 60 * 1000), // 2-12 minutes apart
      walletAddress: generateMockAddress(),
      walletLabel: isCreator ? 'Creator' : isWhale ? 'Whale' : undefined,
      solAmount,
      tokenAmount: solAmount * (1000000 + Math.random() * 5000000), // Rough token amount
      tokenSymbol: 'TOKEN',
      pricePerToken: 0.000001 + Math.random() * 0.00001,
      isCreator,
      bundleSize: type === 'bundled_buy' ? 3 + Math.floor(Math.random() * 5) : undefined,
      bundlePosition: type === 'bundled_buy' ? 1 : undefined,
      holdingPercentAfter: isBuy
        ? 0.5 + Math.random() * 5
        : isCreator
          ? Math.random() * 2
          : Math.random() * 3,
    });
  }

  return transactions.sort((a, b) => b.timestamp - a.timestamp);
}

function generateMockAddress(): string {
  const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < 44; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
